import _slicedToArray from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
import { AppAlertsContext } from '@infl-ts/ui-app-alerts';
import { useFreezer } from '@infl-ts/util-freezer';
import { usePreviousProp } from '@infl-ts/util-hooks';
import { constUndefined, constVoid, flow, pipe } from 'fp-ts/function';
import * as O from 'fp-ts/Option';
import { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router';
import { addActivityIdToUrl, parseChallengePlayerConfig, removeCPSearchParams } from '../challenge-player-url-helpers';
import { advanceToNextStage, closeActivity, continueActivity, destroyConfig, openActivity, startNewActivity, useChallengeReducer } from './challenge-reducer';
import { jsx as _jsx } from "react/jsx-runtime";
export var ChallengePlayerContext = /*#__PURE__*/createContext({
  manuallyOpenChallengePlayer: constVoid,
  closeChallengePlayer: constVoid,
  challengePlayerState: {
    activity: null,
    stage: null,
    open: false,
    challengeConfig: null
  },
  advanceStage: constVoid,
  startNewActivity: constVoid,
  continueActivity: constVoid
});

var handleRemoveCPParams = function handleRemoveCPParams(history, search) {
  var isBackClick = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  return removeCPSearchParams(history, search, isBackClick);
};

var handleCloseChallengePlayer = function handleCloseChallengePlayer(dispatch) {
  dispatch(closeActivity());
  setTimeout(function () {
    return dispatch(destroyConfig());
  }, 0);
};

var isOpeningFromScratch = function isOpeningFromScratch(config, prevConfig) {
  return !config.activity || config.minimized !== (prevConfig === null || prevConfig === void 0 ? void 0 : prevConfig.minimized) || config.activity === (prevConfig === null || prevConfig === void 0 ? void 0 : prevConfig.activity) || config.uuid !== (prevConfig === null || prevConfig === void 0 ? void 0 : prevConfig.uuid);
};

var handleLocationChange = function handleLocationChange(dispatch, location, prevLocation, isPlayerOpen) {
  var prevConfig = pipe(parseChallengePlayerConfig(prevLocation), O.getOrElseW(constUndefined));
  return pipe(parseChallengePlayerConfig(location), O.match(function () {
    return isPlayerOpen && handleCloseChallengePlayer(dispatch);
  }, function (config) {
    return isOpeningFromScratch(config, prevConfig) && dispatch(openActivity(config));
  }));
};

export var ChallengePlayerProvider = function ChallengePlayerProvider(_ref) {
  var _challengePlayerState2;

  var children = _ref.children;

  var _useChallengeReducer = useChallengeReducer(),
      _useChallengeReducer2 = _slicedToArray(_useChallengeReducer, 2),
      challengePlayerState = _useChallengeReducer2[0],
      dispatch = _useChallengeReducer2[1];

  var challengePlayerRef = useRef(challengePlayerState);

  var _useContext = useContext(AppAlertsContext),
      showAlert = _useContext.showAlert;

  var location = useLocation();
  var history = useHistory();
  var prevLocation = usePreviousProp(location);
  useEffect(function () {
    challengePlayerRef.current = challengePlayerState;
  }, [challengePlayerState]);
  useEffect(function () {
    handleLocationChange(dispatch, location, prevLocation, challengePlayerRef.current.open); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);
  var advanceStage = useCallback(function (activity) {
    var stage = challengePlayerRef.current.stage;
    (stage === null || stage === void 0 ? void 0 : stage.notificationMessage) && showAlert({
      severity: 'success',
      message: stage.notificationMessage,
      duration: 'medium'
    });
    activity && addActivityIdToUrl(activity.id);
    dispatch(advanceToNextStage(activity));
  }, [dispatch, challengePlayerRef, showAlert]);
  var manuallyOpenChallengePlayer = useCallback(function (challengeConfig) {
    return dispatch(openActivity(challengeConfig));
  }, [dispatch]);
  useEffect(function () {
    var closeOnBackClick = function closeOnBackClick() {
      if (challengePlayerRef.current.open) handleRemoveCPParams(history, location.search, true);
    };

    window.addEventListener('popstate', closeOnBackClick);
    return function () {
      window.removeEventListener('popstate', closeOnBackClick);
    };
  }, []);

  var _useFreezer = useFreezer(),
      freeze = _useFreezer.freeze,
      unfreeze = _useFreezer.unfreeze;

  useEffect(function () {
    var _challengePlayerState;

    challengePlayerState.open && !((_challengePlayerState = challengePlayerState.challengeConfig) !== null && _challengePlayerState !== void 0 && _challengePlayerState.minimized) ? freeze() : unfreeze();
    return function () {
      unfreeze();
    };
  }, [challengePlayerState.open, freeze, unfreeze, (_challengePlayerState2 = challengePlayerState.challengeConfig) === null || _challengePlayerState2 === void 0 ? void 0 : _challengePlayerState2.minimized]);
  return /*#__PURE__*/_jsx(ChallengePlayerContext.Provider, {
    value: {
      manuallyOpenChallengePlayer: manuallyOpenChallengePlayer,
      challengePlayerState: challengePlayerState,
      advanceStage: advanceStage,
      closeChallengePlayer: function closeChallengePlayer() {
        return handleRemoveCPParams(history, location.search);
      },
      startNewActivity: flow(startNewActivity, dispatch),
      continueActivity: flow(continueActivity, dispatch)
    },
    "data-element": "unknown",
    "data-component": "ChallengePlayerProvider",
    "data-source-file": "challenge-player-context.tsx",
    children: children({
      challengePlayerState: challengePlayerState,
      closeActivity: flow(closeActivity, dispatch),
      startNewActivity: flow(startNewActivity, dispatch),
      continueActivity: flow(continueActivity, dispatch)
    })
  });
};